import {
  useInfiniteQuery,
  InfiniteData,
  hashQueryKey,
} from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { User } from '@opallabs/concepts'
import {
  users,
  OpalApiResponseDocument,
  OpalApiRequestParams,
  findResourceOfType,
} from '../../../../v2'
import { globalQueryKeys } from '../../queryKeys'

type ResponseDocument = OpalApiResponseDocument<'ReadUsersV2', 200>
type NonPageQueryParams = Omit<
  OpalApiRequestParams<'ReadUsersV2'>['query'],
  'page'
>

type GetUsersArgs<TUser extends User> = NonPageQueryParams & {
  limit?: number
  select?: (document?: InfiniteData<ResponseDocument>) => TUser[]
  workspaceId?: string
}

const LIMIT = 100

type FetchUsersParams = NonPageQueryParams & {
  page: number
  limit: number
  workspaceId?: string
}

const defaultSelect = <TUser extends User>(
  document?: InfiniteData<ResponseDocument>
): TUser[] => {
  if (!document) return []
  return document.pages.reduce<TUser[]>((allUsers, page) => {
    const users: TUser[] = page.data.map((user) => {
      const assetReference = page.included?.find(
        findResourceOfType(
          'asset_reference',
          'V2',
          user.relationships.avatar_asset_reference?.data?.id
        )
      )
      return {
        type: 'user' as const,
        uuid: user.attributes.uuid,
        name: user.attributes.full_name,
        asset: assetReference
          ? {
              id: assetReference.attributes.uuid,
              url: assetReference.attributes.full_url ?? undefined,
            }
          : undefined,
        legacyId: user.id,
      } as TUser
    })
    return [...allUsers, ...users]
  }, [])
}

const fetchUsers = ({
  fields,
  filter,
  include,
  page,
  limit,
  workspaceId,
}: FetchUsersParams) =>
  users.read({
    query: {
      fields,
      filter:
        !filter && workspaceId
          ? {
              brand_id: [parseInt(workspaceId, 10)],
            }
          : filter,
      include,
      page: { offset: page * limit, limit },
    },
  })

/**
 * If multiple instances of this query are rerendered at the same time,
 * we can hit a race condition where the same API request is fired off
 * several times. We'll use this Set to store the hashed query keys that
 * that are actively fetching a page to prevent those duplicates.
 */
const activeQuerySet = new Set()

export const usePaginatedUsers = <TUser extends User = User>({
  fields,
  filter,
  include,
  limit = LIMIT,
  select = defaultSelect<TUser>,
  workspaceId,
}: GetUsersArgs<TUser>) => {
  const queryKey = globalQueryKeys.users(workspaceId, {
    fields,
    filter,
    include,
    page: { limit },
  })
  const hashedQueryKey = hashQueryKey(queryKey)

  const { data, isFetchingNextPage, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      refetchInterval: false,
      // Query key is set up above so that we can also hash it
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey,
      queryFn: async ({ pageParam }) => {
        return await fetchUsers({
          page: pageParam ?? 0,
          fields,
          filter,
          include,
          workspaceId,
          limit,
        })
      },
      getNextPageParam: (_lastPage, allPages) => {
        const offset = allPages.length * limit
        if (!allPages[0].meta) return false
        if (allPages[0].meta.total > offset) {
          return allPages.length
        }
        return undefined
      },
    })

  const users = useMemo(() => select(data), [select, data])
  const resultsObject = useMemo(
    () => ({
      fetchingComplete: !hasNextPage && !isFetching,
      users,
    }),
    [hasNextPage, isFetching, users]
  )

  useEffect(() => {
    if (!data || !hasNextPage) return
    if (isFetching || isFetchingNextPage) return

    // This query is already running a fetch in another query instance,
    // but the isFetchingNextPage flag may not yet have rerendered
    if (activeQuerySet.has(hashedQueryKey)) return

    // If we're ready to fetch the next page, register this query instance's key
    // to the set to help prevent duplicate queries from race conditions
    activeQuerySet.add(hashedQueryKey)
    fetchNextPage().finally(() => {
      activeQuerySet.delete(hashedQueryKey)
    })
  }, [
    isFetchingNextPage,
    isFetching,
    data,
    fetchNextPage,
    hasNextPage,
    hashedQueryKey,
  ])

  return resultsObject
}
